export const SCREEN_QUICK_NAV_HISTORY_KEY_PREFIX = "screenQuickNav";
export const SCREEN_QUICK_NAV_HISTORY_VERSION = 1;
export const SCREEN_QUICK_NAV_INACTIVE_TIMEOUT_MS = 30 * 60 * 1000;
export const SCREEN_QUICK_NAV_HEARTBEAT_MS = 60 * 1000;

const SCREEN_QUICK_NAV_PATH_ALIASES = {
    "/finances?tab=draft-invoice-follow-up": "/finances?tab=invoice-follow-up",
};

const getDefaultStorage = () => {
    if (typeof window === "undefined") {
        return null;
    }

    return window.localStorage;
};

const isPlainObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);

const sortObjectKeys = (value) =>
    Object.keys(value)
        .sort()
        .reduce((accumulator, key) => {
            accumulator[key] = value[key];
            return accumulator;
        }, {});

const MAX_DYNAMIC_SCREENS = 50;

export const canonicalizeScreenQuickNavPath = (path) =>
    (typeof path === "string" && SCREEN_QUICK_NAV_PATH_ALIASES[path]) || path;

const mergeNumericPathMap = (value, { strategy = "max" } = {}) => {
    if (!isPlainObject(value)) {
        return {};
    }

    return Object.entries(value).reduce((accumulator, [path, amount]) => {
        if (typeof amount !== "number") {
            return accumulator;
        }

        const canonicalPath = canonicalizeScreenQuickNavPath(path);
        if (strategy === "sum") {
            accumulator[canonicalPath] = (accumulator[canonicalPath] || 0) + amount;
            return accumulator;
        }

        accumulator[canonicalPath] = Math.max(accumulator[canonicalPath] || 0, amount);
        return accumulator;
    }, {});
};

const mergeActiveEntriesByPath = (value) => {
    if (!isPlainObject(value)) {
        return {};
    }

    return Object.entries(value).reduce((accumulator, [path, entries]) => {
        if (!isPlainObject(entries)) {
            return accumulator;
        }

        const canonicalPath = canonicalizeScreenQuickNavPath(path);
        const mergedEntries = accumulator[canonicalPath] || {};

        Object.entries(entries).forEach(([tabId, timestamp]) => {
            if (typeof timestamp !== "number") {
                return;
            }

            mergedEntries[tabId] = Math.max(mergedEntries[tabId] || 0, timestamp);
        });

        accumulator[canonicalPath] = mergedEntries;
        return accumulator;
    }, {});
};

const mergeDynamicScreensByPath = (value) => {
    if (!isPlainObject(value)) {
        return {};
    }

    return Object.entries(value).reduce((accumulator, [path, meta]) => {
        if (isPlainObject(meta) && typeof meta.name === "string") {
            const canonicalPath = canonicalizeScreenQuickNavPath(path);
            if (!accumulator[canonicalPath] || canonicalPath === path) {
                accumulator[canonicalPath] = {
                    name: meta.name,
                    category: meta.category || "Other",
                };
            }
        }
        return accumulator;
    }, {});
};

export const createEmptyScreenQuickNavHistory = () => ({
    version: SCREEN_QUICK_NAV_HISTORY_VERSION,
    openCountByPath: {},
    lastOpenedAtByPath: {},
    lastSeenAtByPath: {},
    lastClosedAtByPath: {},
    activeEntriesByPath: {},
    dynamicScreensByPath: {},
});

export const getScreenQuickNavHistoryKey = (userId) =>
    `${SCREEN_QUICK_NAV_HISTORY_KEY_PREFIX}:${userId || "guest"}`;

export const normalizeScreenQuickNavHistory = (state) => {
    const empty = createEmptyScreenQuickNavHistory();
    if (!isPlainObject(state)) {
        return empty;
    }

    return {
        version: SCREEN_QUICK_NAV_HISTORY_VERSION,
        openCountByPath: mergeNumericPathMap(state.openCountByPath, { strategy: "sum" }),
        lastOpenedAtByPath: mergeNumericPathMap(state.lastOpenedAtByPath),
        lastSeenAtByPath: mergeNumericPathMap(state.lastSeenAtByPath),
        lastClosedAtByPath: mergeNumericPathMap(state.lastClosedAtByPath),
        activeEntriesByPath: mergeActiveEntriesByPath(state.activeEntriesByPath),
        dynamicScreensByPath: mergeDynamicScreensByPath(state.dynamicScreensByPath),
    };
};

export const readScreenQuickNavHistory = (userId, storage = getDefaultStorage()) => {
    if (!storage?.getItem) {
        return createEmptyScreenQuickNavHistory();
    }

    try {
        const raw = storage.getItem(getScreenQuickNavHistoryKey(userId));
        if (!raw) {
            return createEmptyScreenQuickNavHistory();
        }

        return normalizeScreenQuickNavHistory(JSON.parse(raw));
    } catch (_error) {
        return createEmptyScreenQuickNavHistory();
    }
};

export const serializeScreenQuickNavHistory = (state) => {
    const normalizedState = normalizeScreenQuickNavHistory(state);

    return JSON.stringify({
        ...normalizedState,
        openCountByPath: sortObjectKeys(normalizedState.openCountByPath),
        lastOpenedAtByPath: sortObjectKeys(normalizedState.lastOpenedAtByPath),
        lastSeenAtByPath: sortObjectKeys(normalizedState.lastSeenAtByPath),
        lastClosedAtByPath: sortObjectKeys(normalizedState.lastClosedAtByPath),
        activeEntriesByPath: sortObjectKeys(
            Object.entries(normalizedState.activeEntriesByPath).reduce((accumulator, [path, entries]) => {
                accumulator[path] = sortObjectKeys(entries);
                return accumulator;
            }, {}),
        ),
        dynamicScreensByPath: sortObjectKeys(normalizedState.dynamicScreensByPath || {}),
    });
};

export const writeScreenQuickNavHistory = (userId, state, storage = getDefaultStorage()) => {
    if (!storage?.setItem) {
        return normalizeScreenQuickNavHistory(state);
    }

    const normalizedState = normalizeScreenQuickNavHistory(state);

    try {
        storage.setItem(
            getScreenQuickNavHistoryKey(userId),
            serializeScreenQuickNavHistory(normalizedState),
        );
    } catch (_error) {
        // Ignore quota/storage failures; UI should still work in-memory.
    }

    return normalizedState;
};

const prunePathEntries = ({ state, path, now, timeoutMs }) => {
    const entries = state.activeEntriesByPath[path];
    if (!isPlainObject(entries)) {
        delete state.activeEntriesByPath[path];
        return;
    }

    const activeEntries = {};
    const staleTimestamps = [];

    Object.entries(entries).forEach(([tabId, timestamp]) => {
        if (typeof timestamp !== "number") {
            return;
        }

        if (now - timestamp <= timeoutMs) {
            activeEntries[tabId] = timestamp;
        } else {
            staleTimestamps.push(timestamp);
        }
    });

    if (Object.keys(activeEntries).length > 0) {
        state.activeEntriesByPath[path] = activeEntries;
        return;
    }

    delete state.activeEntriesByPath[path];

    if (staleTimestamps.length > 0) {
        const inferredClosedAt = Math.max(...staleTimestamps);
        state.lastClosedAtByPath[path] = Math.max(
            state.lastClosedAtByPath[path] || 0,
            inferredClosedAt,
        );
    }
};

export const pruneInactiveScreenQuickNavHistory = (
    state,
    {
        now = Date.now(),
        timeoutMs = SCREEN_QUICK_NAV_INACTIVE_TIMEOUT_MS,
    } = {},
) => {
    const nextState = normalizeScreenQuickNavHistory(state);

    Object.keys(nextState.activeEntriesByPath).forEach((path) => {
        prunePathEntries({ state: nextState, path, now, timeoutMs });
    });

    return nextState;
};

export const enterScreenQuickNavHistory = (
    state,
    { path, tabId, now = Date.now(), timeoutMs = SCREEN_QUICK_NAV_INACTIVE_TIMEOUT_MS },
) => {
    const canonicalPath = canonicalizeScreenQuickNavPath(path);
    if (!canonicalPath || !tabId) {
        return pruneInactiveScreenQuickNavHistory(state, { now, timeoutMs });
    }

    const nextState = pruneInactiveScreenQuickNavHistory(state, { now, timeoutMs });
    nextState.activeEntriesByPath[canonicalPath] = {
        ...(nextState.activeEntriesByPath[canonicalPath] || {}),
        [tabId]: now,
    };
    nextState.openCountByPath[canonicalPath] = (nextState.openCountByPath[canonicalPath] || 0) + 1;
    nextState.lastOpenedAtByPath[canonicalPath] = now;
    nextState.lastSeenAtByPath[canonicalPath] = now;
    delete nextState.lastClosedAtByPath[canonicalPath];
    return nextState;
};

export const heartbeatScreenQuickNavHistory = (
    state,
    { path, tabId, now = Date.now(), timeoutMs = SCREEN_QUICK_NAV_INACTIVE_TIMEOUT_MS },
) => {
    const canonicalPath = canonicalizeScreenQuickNavPath(path);
    if (!canonicalPath || !tabId) {
        return pruneInactiveScreenQuickNavHistory(state, { now, timeoutMs });
    }

    const nextState = pruneInactiveScreenQuickNavHistory(state, { now, timeoutMs });
    nextState.activeEntriesByPath[canonicalPath] = {
        ...(nextState.activeEntriesByPath[canonicalPath] || {}),
        [tabId]: now,
    };
    nextState.lastSeenAtByPath[canonicalPath] = now;
    delete nextState.lastClosedAtByPath[canonicalPath];
    return nextState;
};

export const leaveScreenQuickNavHistory = (
    state,
    { path, tabId, now = Date.now(), timeoutMs = SCREEN_QUICK_NAV_INACTIVE_TIMEOUT_MS },
) => {
    const canonicalPath = canonicalizeScreenQuickNavPath(path);
    if (!canonicalPath || !tabId) {
        return pruneInactiveScreenQuickNavHistory(state, { now, timeoutMs });
    }

    const nextState = pruneInactiveScreenQuickNavHistory(state, { now, timeoutMs });
    const entries = isPlainObject(nextState.activeEntriesByPath[canonicalPath])
        ? { ...nextState.activeEntriesByPath[canonicalPath] }
        : {};

    delete entries[tabId];

    if (Object.keys(entries).length > 0) {
        nextState.activeEntriesByPath[canonicalPath] = entries;
    } else {
        delete nextState.activeEntriesByPath[canonicalPath];
        nextState.lastClosedAtByPath[canonicalPath] = now;
    }

    return nextState;
};

export const getMostFrequentQuickNavScreens = (
    screens,
    state,
    { limit = 12 } = {},
) => {
    const normalizedState = normalizeScreenQuickNavHistory(state);
    const availableScreens = Array.isArray(screens) ? screens : [];

    return [...availableScreens]
        .sort((left, right) => {
            const leftCount = normalizedState.openCountByPath[left.path] || 0;
            const rightCount = normalizedState.openCountByPath[right.path] || 0;
            if (rightCount !== leftCount) {
                return rightCount - leftCount;
            }

            const leftOpenedAt = normalizedState.lastOpenedAtByPath[left.path] || 0;
            const rightOpenedAt = normalizedState.lastOpenedAtByPath[right.path] || 0;
            if (rightOpenedAt !== leftOpenedAt) {
                return rightOpenedAt - leftOpenedAt;
            }

            return left.name.localeCompare(right.name);
        })
        .slice(0, limit);
};

export const registerDynamicScreen = (state, { path, name, category }) => {
    const canonicalPath = canonicalizeScreenQuickNavPath(path);
    if (!canonicalPath || !name) {
        return normalizeScreenQuickNavHistory(state);
    }

    const nextState = normalizeScreenQuickNavHistory(state);
    nextState.dynamicScreensByPath[canonicalPath] = { name, category: category || "Other" };

    // Evict oldest dynamic screens when exceeding the limit
    const dynamicPaths = Object.keys(nextState.dynamicScreensByPath);
    if (dynamicPaths.length > MAX_DYNAMIC_SCREENS) {
        const sortedByLastSeen = dynamicPaths.sort(
            (a, b) => (nextState.lastSeenAtByPath[a] || 0) - (nextState.lastSeenAtByPath[b] || 0),
        );
        const toRemove = sortedByLastSeen.slice(0, dynamicPaths.length - MAX_DYNAMIC_SCREENS);
        toRemove.forEach((p) => delete nextState.dynamicScreensByPath[p]);
    }

    return nextState;
};

export const getRecentlyClosedQuickNavScreens = (
    screens,
    state,
    {
        currentPath,
        limit = 10,
        now = Date.now(),
        timeoutMs = SCREEN_QUICK_NAV_INACTIVE_TIMEOUT_MS,
    } = {},
) => {
    const normalizedState = pruneInactiveScreenQuickNavHistory(state, { now, timeoutMs });
    const canonicalCurrentPath = canonicalizeScreenQuickNavPath(currentPath);
    const activePaths = new Set(Object.keys(normalizedState.activeEntriesByPath));
    const availableScreens = Array.isArray(screens) ? screens : [];
    const staticPaths = new Set(availableScreens.map((s) => s.path));

    // Static screens that were closed
    const closedStatic = availableScreens.filter(
        (screen) =>
            screen.path !== canonicalCurrentPath &&
            !activePaths.has(screen.path) &&
            Boolean(normalizedState.lastClosedAtByPath[screen.path]),
    );

    // Dynamic screens (projects, clients, etc.) that were closed
    const closedDynamic = Object.entries(normalizedState.dynamicScreensByPath || {})
        .filter(
            ([path]) =>
                path !== canonicalCurrentPath &&
                !activePaths.has(path) &&
                !staticPaths.has(path) &&
                Boolean(normalizedState.lastClosedAtByPath[path]),
        )
        .map(([path, meta]) => ({
            id: `dynamic-${path}`,
            name: meta.name,
            path,
            category: meta.category || "Other",
            keywords: [],
        }));

    return [...closedStatic, ...closedDynamic]
        .sort(
            (left, right) =>
                (normalizedState.lastClosedAtByPath[right.path] || 0) -
                (normalizedState.lastClosedAtByPath[left.path] || 0),
        )
        .slice(0, limit);
};
